import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const GUAC_BASE_URL = process.env.REACT_APP_GUAC_BASE_URL

const getToken = () => localStorage.getItem('guac_token')

const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: GUAC_BASE_URL,
  prepareHeaders: headers => {
    const token = getToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  }
})

const baseQueryWithRedirect = async (args, api, extraOptions) => {
  let url = typeof args === 'string' ? args : args.url
  const token = getToken()

  if (token && !url.includes('token=')) {
    const separator = url.includes('?') ? '&' : '?'
    url = `${url}${separator}token=${token}`
  }

  const updatedArgs =
    typeof args === 'string'
      ? url
      : {
          ...args,
          url
        }

  const result = await baseQueryWithAuth(updatedArgs, api, extraOptions)

  if (result?.error) {
    const { status } = result.error
    if (status === 401) {
      localStorage.removeItem('guac_token')
      window.location.href = '/rdp-servers'
    }
  }

  return result
}

export const GuacamoleApi = createApi({
  reducerPath: 'GuacamoleApi',
  baseQuery: baseQueryWithRedirect,
  tagTypes: ['Auth', 'Connections', 'ActiveSessions', 'Users'],
  endpoints: builder => ({
    login: builder.mutation({
      query: ({ username, password }) => ({
        url: `/api/tokens`,
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `username=${encodeURIComponent(
          username
        )}&password=${encodeURIComponent(password)}`
      }),
      transformResponse: response => ({
        authToken: response.authToken,
        username: response.username,
        dataSource: response.dataSource
      })
    }),

    logout: builder.mutation({
      query: token => ({
        url: `/api/tokens/${token}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Auth', 'Connections', 'ActiveSessions']
    }),

    getConnections: builder.query({
      query: ({ dataSource = 'postgresql' } = {}) =>
        `/api/session/data/${dataSource}/connections`,
      providesTags: ['Connections']
    }),

    getConnectionById: builder.query({
      query: ({ dataSource = 'postgresql', connectionId }) =>
        `/api/session/data/${dataSource}/connections/${connectionId}`,
      providesTags: (r, e, arg) => [
        { type: 'Connections', id: arg.connectionId }
      ]
    }),

    createConnection: builder.mutation({
      query: ({ dataSource = 'postgresql', connection }) => ({
        url: `/api/session/data/${dataSource}/connections`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: connection
      }),
      invalidatesTags: ['Connections']
    }),

    updateConnection: builder.mutation({
      query: ({ dataSource = 'postgresql', connectionId, connection }) => ({
        url: `/api/session/data/${dataSource}/connections/${connectionId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: connection
      }),
      invalidatesTags: (r, e, arg) => [
        { type: 'Connections', id: arg.connectionId }
      ]
    }),

    deleteConnection: builder.mutation({
      query: ({ dataSource = 'postgresql', connectionId }) => ({
        url: `/api/session/data/${dataSource}/connections/${connectionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Connections']
    }),

    getActiveConnections: builder.query({
      query: ({ dataSource = 'postgresql' } = {}) =>
        `/api/session/data/${dataSource}/activeConnections`,
      providesTags: ['ActiveSessions']
    }),

    terminateSession: builder.mutation({
      query: ({ dataSource = 'postgresql', connectionIdentifier }) => ({
        url: `/api/session/data/${dataSource}/activeConnections/${connectionIdentifier}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ActiveSessions']
    }),

    createTunnel: builder.mutation({
      query: (payload = {}) => ({
        url: `/api/tunnels`,
        method: 'POST',
        body: payload
      })
    }),

    getUsers: builder.query({
      query: ({ dataSource = 'postgresql' } = {}) =>
        `/api/session/data/${dataSource}/users`,
      providesTags: ['Users']
    }),

    getUserDetails: builder.query({
      query: ({ dataSource = 'postgresql', username }) =>
        `/api/session/data/${dataSource}/users/${encodeURIComponent(username)}`,
      providesTags: (r, e, arg) => [{ type: 'Users', id: arg.username }]
    })
  })
})

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetConnectionsQuery,
  useLazyGetActiveConnectionsQuery,
  useGetConnectionByIdQuery,
  useCreateConnectionMutation,
  useUpdateConnectionMutation,
  useDeleteConnectionMutation,
  useGetActiveConnectionsQuery,
  useTerminateSessionMutation,
  useCreateTunnelMutation,
  useGetUsersQuery,
  useGetUserDetailsQuery
} = GuacamoleApi
